import { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";

import {
  DEFAULT_SERVICE_CATEGORY_SECTION,
  ServiceCategory,
  ServiceCategorySection,
} from "features/serviceCategory/model/serviceCategory";
import {
  useCreateServiceCategoryMutation,
  useLazyGetServiceCategoryListQuery,
  useUpdateServiceCategoryMutation,
} from "features/serviceCategory/api/serviceCategoryApi";
import { useRemoveMutation } from "features/handbook/api/handbookApi";
import { useConfirmDelete } from "shared/hooks/useConfirmDelete";
import { ALL_SERVICES_CODE, SERVICE_CATEGORY_CODE } from "./useHandbookRoute";

type Args = {
  dictCode?: string;
  serviceCode?: string;
  page: number;
  pageSize: number;
  debouncedSearch: string;
  level: number;
  ancestorId: number | null;
  section: ServiceCategorySection;
};

/**
 * Категории услуг — дерево с картинками, поэтому у них отдельные списки,
 * карточки вместо таблицы и форма, отправляющая `multipart/form-data`.
 */
export function useServiceCategorySection({
  dictCode,
  serviceCode,
  page,
  pageSize,
  debouncedSearch,
  level,
  ancestorId,
  section,
}: Args) {
  const confirmDelete = useConfirmDelete();

  const [createServiceCategory, { isLoading: isCreating }] =
    useCreateServiceCategoryMutation();
  const [updateServiceCategory, { isLoading: isUpdating }] =
    useUpdateServiceCategoryMutation();
  const [remove] = useRemoveMutation();

  const [fetchAll, { data: allData, isFetching: isFetchingAll }] =
    useLazyGetServiceCategoryListQuery();
  const [fetchForService, { data: serviceData, isFetching: isFetchingService }] =
    useLazyGetServiceCategoryListQuery();

  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState<ServiceCategory | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<ServiceCategory | null>(null);

  const isAllServices = serviceCode === ALL_SERVICES_CODE;

  const allFilter = useMemo(
    () => ({
      page: page ?? 1,
      size: pageSize,
      deleted: false,
      name: debouncedSearch || "",
      level,
      ancestorId,
      // Основной раздел бэк отдаёт по умолчанию, `section` шлём только для остальных.
      ...(section !== DEFAULT_SERVICE_CATEGORY_SECTION ? { section } : {}),
    }),
    [page, pageSize, debouncedSearch, level, ancestorId, section],
  );

  const serviceFilter = useMemo(
    () => ({
      ...allFilter,
      ...(serviceCode && !isAllServices
        ? { serviceCode: String(serviceCode) }
        : {}),
    }),
    [allFilter, serviceCode, isAllServices],
  );

  useEffect(() => {
    if (dictCode !== SERVICE_CATEGORY_CODE) return;

    if (serviceCode && !isAllServices) fetchForService(serviceFilter);
    else fetchAll(allFilter);
  }, [
    dictCode,
    serviceCode,
    isAllServices,
    fetchForService,
    fetchAll,
    serviceFilter,
    allFilter,
  ]);

  const openView = useCallback((row: ServiceCategory) => {
    setViewRow(row);
    setViewOpen(true);
  }, []);

  const closeView = useCallback(() => {
    setViewOpen(false);
    setViewRow(null);
  }, []);

  const openEdit = useCallback((row: ServiceCategory) => {
    setEditingRow({ ...row });
    setEditOpen(true);
  }, []);

  const openCreate = useCallback(() => {
    setEditingRow(null);
    setEditOpen(true);
  }, []);

  const closeEdit = useCallback(() => setEditOpen(false), []);

  const handleSubmit = useCallback(
    async (formData: FormData) => {
      try {
        if (editingRow?.id) {
          await updateServiceCategory(formData).unwrap();
          message.success("Категория обновлена");
        } else {
          await createServiceCategory(formData).unwrap();
          message.success("Категория создана");
        }

        setEditOpen(false);
        await fetchAll(allFilter);
      } catch (e) {
        console.error(e);
        message.error("Не удалось сохранить категорию");
      }
    },
    [
      editingRow?.id,
      createServiceCategory,
      updateServiceCategory,
      fetchAll,
      allFilter,
    ],
  );

  const handleDelete = useCallback(
    (row: ServiceCategory) => {
      if (!dictCode) return;

      confirmDelete({
        content: row.nameRu || row.nameKy || `#${row.id}`,
        onOk: async () => {
          try {
            await remove({ slug: dictCode, id: row.id! }).unwrap();
            message.success("Запись удалена");
            await fetchAll(allFilter);
          } catch {
            message.error("Не удалось удалить");
          }
        },
      });
    },
    [confirmDelete, remove, dictCode, fetchAll, allFilter],
  );

  return {
    cards: isAllServices
      ? (allData?.res?.content ?? [])
      : (serviceData?.res?.content ?? []),
    cardsTotal: isAllServices
      ? (allData?.res?.totalElements ?? 0)
      : (serviceData?.res?.totalElements ?? 0),
    isFetching: isAllServices ? isFetchingAll : isFetchingService,
    isSaving: isCreating || isUpdating,
    section,
    viewOpen,
    viewRow,
    editOpen,
    editingRow,
    openView,
    closeView,
    openEdit,
    openCreate,
    closeEdit,
    handleSubmit,
    handleDelete,
  };
}
